import ConstList from '../ConstList'
import { usesAttendance2 } from '../utils/rosterActualPolicy'

/**
 * 勤務表の勤怠区分の表。
 *
 * **区分の名前と入力の決まりは、ここ1か所に書く。**勤怠1・勤怠2の選択肢、
 * 入力手順、勤怠1・勤怠2の説明、勤怠2を選ばないと保存できないか、
 * 時刻の欄を空にするかは、全部この表から作る。
 *
 * 2026-09-30 の洗い出しで、午前欠勤・午後欠勤が選択肢にはあるのに、入力手順と
 * 説明に載っていなかった。名前が選択肢・入力手順・説明の3か所に別々に
 * 書かれていたため。
 *
 * - opensAttendance2: 選ぶと勤怠2（午後側）を押せるようにする。
 *   **この印は、ここでは書かない。**勤怠2を使う区分の並びは utils/rosterActualPolicy.js の
 *   usesAttendance2 に1か所で持っていて、勤務表の入力制御（Roster.vue の afternoon）と
 *   ファイル取り込みの確認（utils/rosterImport.js）が同じ並びを見る。ここへも書くと、
 *   同じ並びが2か所になる。表を作るときに、その並びから付ける。
 * - requiresAttendance2: 勤怠2を選ばないと保存できない。シフトは勤怠2を
 *   押せるようにするが、選ぶと勤怠2が自動で「シフト」になるので求めない
 * - clearsTimes: 選ぶと時刻の欄を空にし、定時の候補も出さない
 *
 * **requiresAttendance2 の並びは、back の RosterAttendance2Policy と同じにする。**
 * 片方だけ変えると、画面は通すのに保存で断られる（または、その逆）。
 *
 * 並び順は選択肢の並び順。
 */
const ATTENDANCE1_ROWS = [
  { id: ConstList.FULLTIME, name: '常勤' },
  { id: ConstList.HOLIDAY, name: '休日', clearsTimes: true },
  { id: ConstList.SHIFT, name: 'シフト' },
  { id: ConstList.MORNING_WORK, name: '午前出勤', requiresAttendance2: true },
  { id: ConstList.EARLY_MORNING, name: '早出出勤', requiresAttendance2: true },
  { id: ConstList.PAID, name: '有給', clearsTimes: true },
  { id: ConstList.MORNING_PAID, name: '午前有給', requiresAttendance2: true },
  { id: ConstList.SUBSTITUTE, name: '代休', clearsTimes: true },
  { id: ConstList.MORNING_SUBSTITUTE, name: '午前代休', requiresAttendance2: true },
  { id: ConstList.DEDUCTION, name: '欠勤', clearsTimes: true },
  { id: ConstList.MORNING_DEDUCTION, name: '午前欠勤', requiresAttendance2: true },
  { id: ConstList.SPECIAL_HOLIDAY, name: '特休有給', clearsTimes: true },
  { id: ConstList.MORNING_SPECIAL_HOLIDAY, name: '午前特休', requiresAttendance2: true }
]

export const ATTENDANCE1_KINDS = Object.freeze(ATTENDANCE1_ROWS.map(row => Object.freeze({
  ...row,
  ...(usesAttendance2(row.id) ? { opensAttendance2: true } : {})
})))

/**
 * 勤怠2（午後側・終業側）の区分。help は「勤怠2の説明」に出す1行。
 *
 * **午後欠勤の 16 は、勤怠1の早出出勤と同じ番号。**列が違うので別の区分。
 */
export const ATTENDANCE2_KINDS = Object.freeze([
  Object.freeze({ id: ConstList.AFTERNOON_WORK, name: '午後出勤', help: '午後は通常出勤' }),
  Object.freeze({
    id: ConstList.SHIFT,
    name: 'シフト',
    help: '終業時間の変更がある日（勤怠1が「シフト」のときは自動設定）'
  }),
  Object.freeze({ id: ConstList.AFTERNOON_PAID, name: '午後有給', help: '午後が有給' }),
  Object.freeze({ id: ConstList.AFTERNOON_SUBSTITUTE, name: '午後代休', help: '午後が代休' }),
  // 午後欠勤は、選択肢にあるのに説明に無かった（説明の1行は 2026-10-09 に決めた）。
  Object.freeze({ id: ConstList.AFTERNOON_DEDUCTION, name: '午後欠勤', help: '午後が欠勤' }),
  Object.freeze({ id: ConstList.AFTERNOON_SPECIAL_HOLIDAY, name: '午後特休', help: '午後が特休' })
])

const kindsWhere = test => ATTENDANCE1_KINDS.filter(test)
const idsOf = kinds => Object.freeze(kinds.map(kind => kind.id))

/** 選ぶと勤怠2を押せるようにする勤怠1。 */
export const OPENS_ATTENDANCE2_IDS = idsOf(kindsWhere(kind => kind.opensAttendance2))

/** 勤怠2を選ばないと保存できない勤怠1。 */
export const REQUIRES_ATTENDANCE2_IDS = idsOf(kindsWhere(kind => kind.requiresAttendance2))

/** 選ぶと時刻の欄を空にし、定時の候補を出さない勤怠1（休日・終日の休み）。 */
export const CLEARS_TIMES_IDS = idsOf(kindsWhere(kind => kind.clearsTimes))

const quoted = kinds => kinds.map(kind => '「' + kind.name + '」').join('')

/**
 * 入力手順と勤怠1・勤怠2の説明に並べる名前（「」でくくって並べたもの）。
 *
 * 勤怠1の説明は、区分を4つの組に分けて書く。どの区分もどれか1つの組に入る
 * （表に区分を足すと、説明のどこかに必ず出る）。
 */
export const ATTENDANCE_GUIDE_NAMES = Object.freeze({
  // 勤怠2（午後側）も選ぶ区分。入力手順と「勤怠2の説明」の※に出す。
  opensAttendance2: quoted(kindsWhere(kind => kind.opensAttendance2)),
  // 選ぶと時刻の欄が空になる区分。
  clearsTimes: quoted(kindsWhere(kind => kind.clearsTimes)),
  // 休日出勤の入れ方に出す名前。
  holiday: quoted(kindsWhere(kind => kind.id === ConstList.HOLIDAY)),
  // 勤怠1の説明の4つの組。
  work: quoted(kindsWhere(kind =>
    !kind.opensAttendance2 && !kind.requiresAttendance2 && !kind.clearsTimes)),
  halfDay: quoted(kindsWhere(kind => kind.requiresAttendance2)),
  shift: quoted(kindsWhere(kind => kind.opensAttendance2 && !kind.requiresAttendance2))
})

const isBlank = value => value === null || value === undefined || value === ''

/**
 * その行で、勤怠2を選ばないと保存できないか。
 *
 * **行ごとに、その行の勤怠1で決める。**前は全部の行を for で回して上書きした
 * 最後の日の値を、全部の行に渡していた。月末が午前系でない月は午前有給の日も
 * 勤怠2が空のまま保存でき、月末が午前系の月は押せない勤怠2まで必須になって
 * どう直しても保存できなかった。
 *
 * **前に保存した行を、勤怠1を変えずに保存し直すときは求めない。**前の不具合で
 * 勤怠2が空のまま保存できていた行がある。その月を開き直して備考だけ直したとき、
 * 触っていない行で断ると、その月は勤怠2を選び直すまで保存できなくなる。
 * back の保存の口（RosterAttendance2Policy）も同じ決まりで断る。
 *
 * @param attendance1 いまの勤怠1
 * @param savedRow 読み込んだときの行（{ id, attendance1, attendance2 }）。まだ保存していない行は id が null
 */
export function attendance2IsRequired ({ attendance1, savedRow }) {
  if (!REQUIRES_ATTENDANCE2_IDS.includes(attendance1)) return false
  const unchangedSavedRow = Boolean(savedRow) &&
    savedRow.id !== null && savedRow.id !== undefined &&
    savedRow.attendance1 === attendance1 &&
    isBlank(savedRow.attendance2)
  return !unchangedSavedRow
}
